import storage from 'Helpers/storage';
import { __DEV__, USER_INFO_STORAGE_KEY, PANTHER_CONFIG } from 'Source/constants';
import { UserInfo } from 'Components/utils/AuthContext';
import { getActorDisplayName } from 'Helpers/utils';
import * as Sentry from '@sentry/browser';
import { datadogRum } from '@datadog/browser-rum';
import { AppConfig } from 'Generated/schema';
import { sentryBeforeSend } from './sentryBeforeSendFilter';

interface ErrorData {
  extras?: {
    [key: string]: any;
  };
  fingerprint?: string[];
}

// Get the currently active user without relying to React
const getErrorUser = (): Sentry.User | undefined => {
  // Fetch the user from the localstorage. This helps identify users even when they got logged out
  // because of a need to re-authenticate
  const user = storage.local.read<UserInfo>(USER_INFO_STORAGE_KEY);
  if (!user) {
    return undefined;
  }

  return {
    id: user.id,
    name: getActorDisplayName(user),
    email: user.email,
  };
};

/**
 * Logs an error to sentry. Accepts *optional* additional arguments for easier debugging
 */
export const logError = (error: Error, { extras, fingerprint }: ErrorData = {}) => {
  Sentry.captureException(error, {
    // we are re-assigning the user to account for the fact that it might have changed from the time
    // Sentry was initialized.
    user: getErrorUser(),
    // If we have any additional unstructured data we add it here
    extra: extras,
    // group errors together based on a set of parameters
    fingerprint,
  });

  // Forward to Datadog RUM so Sentry events can be correlated to session replays via @session.id.
  // Guard against RUM not being initialized (e.g. datadogRumEnabled=false or very early in boot).
  if (datadogRum.getInternalContext()) {
    datadogRum.addError(error, extras);
  }
};

/**
 * Initializes Sentry in order to catch unhandled exceptions that our code doesn't already handle
 */
export const initializeErrorTracking = () => {
  const sentryDsn = PANTHER_CONFIG.SENTRY_DSN;
  const sentryRelease = PANTHER_CONFIG.PANTHER_VERSION;
  const env = PANTHER_CONFIG.ENVIRONMENT;

  Sentry.init({
    dsn: sentryDsn,
    enabled: !__DEV__,
    release: env === 'dev' ? null : sentryRelease, // don't set a release for dev environments
    environment: env,
    // Leave this as is. The value `7` should not change. Read the following links to understand why
    // https://github.com/getsentry/sentry-javascript/issues/2539#issuecomment-616638746
    // https://github.com/getsentry/sentry/blob/262eef0b3d6bf4964dceb15865c34a15265a1bae/src/sentry/static/sentry/app/bootstrap/initializeSdk.tsx#L21
    normalizeDepth: 7,
    beforeSend: sentryBeforeSend,
    // Benign cancelled-fetch / GraphQL abort noise is filtered only in sentryBeforeSend (see
    // sentryBeforeSendFilter.ts: isBenignCanceledRequest), not duplicated in ignoreErrors below.
    ignoreErrors: [
      // Ignore SDL/SDYAML parsing errors
      /\(SD\d{8}\)/i,
      // Ignore monaco-yaml errors
      /Unexpected usage.*\s*.*react-monaco-editor-react/i,
      // Ignore Monaco editor line range errors
      /startLineNumber.*cannot be after endLineNumberExclusive/i,
      // Ignore Monaco editor illegal line number errors
      /Illegal value for lineNumber/i,
      // Monaco diff editor: async worker sometimes has no diff yet (third-party, not actionable)
      /no diff result available/i,
      // Monaco editor hit-test crash on Firefox when caretPositionFromPoint returns null offsetNode
      /can't access property "offsetNode"/i,
    ],
  });

  Sentry.setTags({
    type: 'web',
    // Add deployment information as tags
    aws_region: PANTHER_CONFIG.AWS_REGION,
    debug_enabled: PANTHER_CONFIG.DEBUG,
  });

  Sentry.setUser(getErrorUser());
};

// We need to set the tags after the user is authenticated and the app config is fetched
export const setPostAuthorizationSentryTags = (appConfig: AppConfig) => {
  // Empty tags are not allowed that's why we conditionally add some
  Sentry.setTags({
    type: 'web',
    aws_region: PANTHER_CONFIG.AWS_REGION,
    debug_enabled: PANTHER_CONFIG.DEBUG,
    aws_account_id: appConfig.accountId,
    aws_partition: appConfig.awsPartition,
    commit_sha: appConfig.pantherCommit,
    data_lake: appConfig.pantherDataLakeType,
    ...(appConfig.awsAccountAlias && {
      aws_account_alias: appConfig.awsAccountAlias,
    }),
    ...(appConfig.awsOrgId && {
      aws_org_id: appConfig.awsOrgId,
    }),
    ...(appConfig.customerName &&
      PANTHER_CONFIG.ENVIRONMENT !== 'dev' && {
        customer_name: appConfig.customerName,
      }),
  });
};
